/**
 * DAoC Skill Engine - Handles all 43 Spec Skills across 6 Classes (7~8 slots),
 * Level 1~60 scaling, Down-ranking, Monk Base Buff + Cleric Spec Buff stacking,
 * Friendly Fire, Cast Bars, Disruption, and Self-Skill progression.
 */

import { EntityState, DamageType, ActiveBuff } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { EventBus } from '../../core/EventBus';
import {
  CLASS_SKILL_DEFINITIONS,
  SKILL_DEFINITIONS,
  SkillDefinition,
  getScaledSkillMetrics,
} from './CombatTypes';
import { ThreatTable } from './ThreatTable';
import { CrowdControl } from './CCStateMachine';
import { Concentration } from './ConcentrationPool';
import {
  getEffectiveSkillLevel,
  getDerivedCombatMetrics,
  recalculateEntityStats,
  practiceSelfSkill,
} from '../character/CharacterSystem';

export class SkillEngine {
  public static isEntityAdmin(entity: EntityState): boolean {
    return (
      entity.role === 'admin' ||
      (entity as any).isAdmin === true ||
      (entity.job && (entity.job.includes('관리자') || entity.job.includes('admin') || entity.job.includes('국왕') || entity.job.includes('Lord British'))) ||
      entity.appearance?.isKing === true
    );
  }

  public static isEntityNovice(entity: EntityState): boolean {
    return (
      entity.job?.includes('무직') ||
      entity.job?.includes('초보자') ||
      entity.job?.includes('성인식') ||
      false
    );
  }

  /**
   * Execute skill by slot number (1-8) for player or entity based on role
   */
  public useSkill(
    caster: EntityState,
    slot: number,
    registry: EntityRegistry
  ): boolean {
    if (caster.isDead) return false;
    if (!CrowdControl.canAct(caster)) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚠️ [${caster.name}] 기절/수면 상태에서는 스킬을 사용할 수 없습니다.`,
        category: 'system',
      });
      return false;
    }

    const isAdmin = SkillEngine.isEntityAdmin(caster);
    const isNovice = !isAdmin && SkillEngine.isEntityNovice(caster);

    let skillSet = CLASS_SKILL_DEFINITIONS[caster.role] || CLASS_SKILL_DEFINITIONS.tank;
    if (isAdmin) {
      skillSet = CLASS_SKILL_DEFINITIONS.admin;
    } else if (isNovice) {
      skillSet = CLASS_SKILL_DEFINITIONS.novice;
    }

    const def = skillSet[slot];
    if (!def) {
      if (isNovice && slot > 1) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⚠️ [초보자] 아직 직업을 갖지 않은 초보자는 기본 공격(1번)만 사용할 수 있습니다. 성내 직업 교관을 찾아가 전직하세요!`,
          category: 'system',
        });
      }
      return false;
    }

    // 관리자 스킬 보안 차단: 관리자가 아닌 일반 캐릭이나 몬스터가 관리자 스킬을 사용하려 하면 즉시 차단
    if (skillSet === CLASS_SKILL_DEFINITIONS.admin && !isAdmin) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🚫 [권한 부족] 관리자 전용 스킬은 일반 플레이어 및 몬스터가 사용할 수 없습니다.`,
        category: 'system',
      });
      return false;
    }

    const effLevel = getEffectiveSkillLevel(caster, slot);
    const scaled = getScaledSkillMetrics(def, effLevel);
    const metrics = getDerivedCombatMetrics(caster);

    // Apply weapon skill SP reduction (-0.4% per %, up to -40%)
    const actualSpCost = isAdmin ? 0 : Math.max(0, Math.round(scaled.spCost * metrics.spCostMult));
    const actualMpCost = isAdmin ? 0 : scaled.mpCost;

    // Check cooldown (관리자는 쿨타임 없음)
    if (!isAdmin && caster.cooldowns[slot] && caster.cooldowns[slot] > 0) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⏳ [${def.nameKr} Lv.${effLevel}] 재사용 대기시간 (${caster.cooldowns[slot].toFixed(1)}초 남음)`,
        category: 'system',
      });
      return false;
    }

    // Check resources
    if (caster.resources.sp < actualSpCost) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚠️ [${caster.name}] 기력(SP) 부족! (필요: ${actualSpCost} SP)`,
        category: 'system',
      });
      return false;
    }
    if (caster.resources.mp < actualMpCost) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚠️ [${caster.name}] 마나(MP) 부족! (필요: ${actualMpCost} MP)`,
        category: 'system',
      });
      return false;
    }

    // Determine target
    let target = caster.targetId ? registry.get(caster.targetId) || null : null;

    // Raise Dead (Paladin Slot 7 / Monk Slot 8) targets dead allies!
    const isRaiseDead = def.name === 'Raise Dead';
    if (!isRaiseDead && target && target.isDead) {
      target = null;
      caster.targetId = null;
    }

    // Handle toggle Concentration buffs/songs/chants
    if (def.isToggle) {
      return this.handleToggleSkill(caster, target, slot, def, scaled.concCost, actualMpCost, effLevel, registry);
    }

    // Handle casted skills (castTime > 0)
    if (def.castTime > 0) {
      const actualCastTime = Number(Math.max(0.4, def.castTime * metrics.castTimeMult).toFixed(2));

      if (isRaiseDead) {
        // Find dead party member if target isn't dead ally
        if (!target || !target.isDead || target.category === 'monster') {
          const deadAlly = registry.getParty().find((p) => p.isDead);
          if (!deadAlly) {
            EventBus.emit('LOG_EMITTED', {
              id: Math.random().toString(),
              timestamp: performance.now(),
              text: `✨ [${def.nameKr}] 주변에 부활시킬 쓰러진 파티원이 없습니다!`,
              category: 'system',
            });
            return false;
          }
          target = deadAlly;
        }
      } else if (caster.role === 'healer' && (slot === 1 || slot === 4)) {
        // Holy Hand / Cleansing: default to self if no friendly target
        if (!target || target.category === 'monster') target = caster;
      } else {
        // Offensive cast (Meteor, Rock Throw, Chain Lightning, Frost Wind, Critical Shot, Whisper, Lullaby)
        if (!target) {
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `⚠️ [${def.nameKr}] 대상을 먼저 타겟팅하세요! (Tab 또는 클릭)`,
            category: 'system',
          });
          return false;
        }
        if (registry.getDistance(caster, target) > scaled.range) {
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `⚠️ [${def.nameKr}] 대상이 사거리 밖입니다! (${scaled.range}타일)`,
            category: 'system',
          });
          return false;
        }
      }

      caster.resources.sp -= actualSpCost;
      caster.resources.mp -= actualMpCost;

      caster.cast = {
        skillId: slot,
        skillName: `${def.nameKr} (Lv.${effLevel})`,
        targetId: target ? target.id : caster.id,
        castTime: actualCastTime,
        progress: 0,
        damageType: def.damageType,
        castLevel: effLevel,
      };

      EventBus.emit('CAST_STARTED', {
        entityId: caster.id,
        spellName: `${def.nameKr} Lv.${effLevel}`,
        castTime: actualCastTime,
        targetId: target ? target.id : caster.id,
      });
      return true;
    }

    // Instant skills
    return this.executeInstantSkill(caster, target, slot, def, scaled, actualSpCost, actualMpCost, effLevel, registry);
  }

  private handleToggleSkill(
    caster: EntityState,
    target: EntityState | null,
    slot: number,
    def: SkillDefinition,
    concCost: number,
    mpCost: number,
    effLevel: number,
    registry: EntityRegistry
  ): boolean {
    const scaled = getScaledSkillMetrics(def, effLevel);
    const metrics = getDerivedCombatMetrics(caster);
    const buffTarget =
      def.range > 0 && target && target.category !== 'monster' && target.category !== 'resource'
        ? target
        : caster;

    const buffId = `toggle_${caster.role}_${slot}_${buffTarget.id}`;

    // Build DAoC buff payload
    const buff: ActiveBuff = {
      id: buffId,
      name: `${def.nameKr} Lv.${effLevel}`,
      sourceId: caster.id,
      concCost,
      duration: -1,
      elapsed: 0,
      icon: def.icon,
    };

    const pwr = Math.round(scaled.power * metrics.buffPotencyMult);

    // 1. Paladin Toggles (Slot 3: Healing Chant, Slot 5: Vigor)
    if (caster.role === 'tank') {
      if (slot === 3) {
        buff.hpRegenBonus = Number((pwr / 3.0).toFixed(1)); // Tick heal per 3s converted to per-sec regen
        buff.buffCategory = 'chant';
      } else if (slot === 5) {
        buff.spRegenBonus = scaled.power;
        buff.buffCategory = 'chant';
      }
    }
    // 2. Ranger Self Toggles (Slot 3: 바람살, Slot 4: 고무인간)
    else if (caster.role === 'ranged_dps') {
      if (slot === 3) {
        buff.castSpeedBonus = Math.min(0.55, scaled.power / 100);
        buff.attackSpeedBonus = Math.min(0.55, scaled.power / 100);
        buff.buffCategory = 'self_toggle';
      } else if (slot === 4) {
        buff.evadeBonus = Math.min(0.50, scaled.power / 100);
        buff.buffCategory = 'self_toggle';
      }
    }
    // 3. Wizard Self Toggle (Slot 5: 정신집중)
    else if (caster.role === 'caster_dps' && slot === 5) {
      buff.critBonus = Math.min(0.45, scaled.power / 100);
      buff.buffCategory = 'self_toggle';
    }
    // 4. Cleric DAoC Spec Composite Buffs (Slot 5: 힘+체력, Slot 6: 민첩+속도, Slot 7: 지/신/매)
    else if (caster.role === 'healer') {
      buff.buffCategory = 'spec_composite';
      if (slot === 5) buff.statBonuses = { str: pwr, con: pwr };
      else if (slot === 6) buff.statBonuses = { dex: pwr, qui: pwr };
      else if (slot === 7) buff.statBonuses = { int: pwr, pie: pwr, cha: pwr };
      practiceSelfSkill(caster, 'prayer', 0.4);
    }
    // 5. Bard Songs (Slot 3: 집중의노래, Slot 4: 재생의노래, Slot 5: 행군의노래)
    else if (caster.role === 'buffer') {
      buff.buffCategory = 'chant';
      if (slot === 3) buff.mpRegenBonus = scaled.power;
      else if (slot === 4) buff.hpRegenBonus = scaled.power;
      else if (slot === 5) buff.moveSpeedBonus = scaled.power / 100;
      practiceSelfSkill(caster, 'performance', 0.4);
    }
    // 6. Monk Self Toggle & DAoC Base Single Buffs (Slot 3: 무념무상, Slot 4: 방어, Slot 5: 힘, Slot 6: 민첩, Slot 7: 체력)
    else if (caster.role === 'monk_hybrid') {
      if (slot === 3) {
        buff.attackSpeedBonus = Math.min(0.55, scaled.power / 100);
        buff.buffCategory = 'self_toggle';
      } else if (slot === 4) {
        buff.damageReduction = Math.min(0.40, scaled.power / 100);
        buff.buffCategory = 'base_single';
      } else if (slot === 5) {
        buff.statBonuses = { str: pwr };
        buff.buffCategory = 'base_single';
      } else if (slot === 6) {
        buff.statBonuses = { dex: pwr };
        buff.buffCategory = 'base_single';
      } else if (slot === 7) {
        buff.statBonuses = { con: pwr };
        buff.buffCategory = 'base_single';
      }
      practiceSelfSkill(caster, 'prayer', 0.3);
    }

    const success = Concentration.tryReserve(caster, buffTarget, buff);
    if (success && mpCost > 0) {
      caster.resources.mp = Math.max(0, caster.resources.mp - mpCost);
    }
    caster.cooldowns[slot] = def.cooldown;

    // Recalculate target's effective stats (DAoC Base + Spec Buff stacking!)
    recalculateEntityStats(buffTarget, false);

    // If party-wide song/chant (Paladin Slot 3/5, Bard Slot 3/4/5), mirror onto nearby party members
    if (
      (caster.role === 'tank' && (slot === 3 || slot === 5)) ||
      (caster.role === 'buffer' && (slot === 3 || slot === 4 || slot === 5))
    ) {
      const isNowActive = buffTarget.buffs.some((b) => b.id === buffId);
      for (const ally of registry.getParty()) {
        if (ally.id === buffTarget.id) continue;
        const allyBuffId = `aura_${caster.role}_${slot}`;
        ally.buffs = ally.buffs.filter((b) => b.id !== allyBuffId);
        if (isNowActive) {
          ally.buffs.push({
            ...buff,
            id: allyBuffId,
            concCost: 0,
          });
        }
      }
    }

    EventBus.emit('SPELL_EFFECT_TRIGGERED', {
      type: 'buff_aura',
      gx: buffTarget.x,
      gy: buffTarget.y,
      color: '#38bdf8',
    });

    return success;
  }

  private executeInstantSkill(
    caster: EntityState,
    target: EntityState | null,
    slot: number,
    def: SkillDefinition,
    scaled: ReturnType<typeof getScaledSkillMetrics>,
    actualSpCost: number,
    actualMpCost: number,
    effLevel: number,
    registry: EntityRegistry
  ): boolean {
    caster.attackAnimTimer = 0.28;
    const metrics = getDerivedCombatMetrics(caster);
    const isAdmin = SkillEngine.isEntityAdmin(caster);
    const isNovice = !isAdmin && SkillEngine.isEntityNovice(caster);

    // ========================================================================
    // 1. Non-Targeted / AoE / Self Instant Skills
    // ========================================================================

    // [팔라딘 Slot 1: 휘두르기] & [수도승 Slot 1: 선풍각] (PBAoE)
    if ((caster.role === 'tank' || caster.role === 'monk_hybrid') && slot === 1) {
      caster.resources.sp -= actualSpCost;
      caster.cooldowns[slot] = scaled.cooldown;

      EventBus.emit('SPELL_EFFECT_TRIGGERED', {
        type: caster.role === 'monk_hybrid' ? 'kick_whirl' : 'cleave_arc',
        gx: caster.x,
        gy: caster.y,
        radius: scaled.range,
      });

      const dmg = Math.round(scaled.power * metrics.physDamageMult);
      for (const m of registry.getMonsters()) {
        if (!m.isDead && registry.getDistance(caster, m) <= scaled.range) {
          this.applyDamage(caster, m, dmg, def.damageType, false, registry);
        }
      }
      practiceSelfSkill(caster, caster.weaponType === 'slash' ? 'slash' : 'crush', 0.4);
      return true;
    }

    // [팔라딘 Slot 2: 타운트]
    if (caster.role === 'tank' && slot === 2) {
      caster.resources.sp -= actualSpCost;
      caster.cooldowns[slot] = scaled.cooldown;

      let affected = 0;
      for (const m of registry.getMonsters()) {
        if (!m.isDead && registry.getDistance(caster, m) <= scaled.range) {
          ThreatTable.taunt(m, caster.id, scaled.power);
          affected++;
        }
      }
      EventBus.emit('SPELL_EFFECT_TRIGGERED', {
        type: 'taunt_burst',
        gx: caster.x,
        gy: caster.y,
        radius: scaled.range,
      });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `📢 [${caster.name}] [타운트 Lv.${effLevel}] 발동! 적 ${affected}체 도발 (유지 ${scaled.duration}초, 어그로 +${scaled.power})`,
        category: 'threat',
      });
      return true;
    }

    // [레인저 Slot 1: 멀티샷] (10레벨마다 화살 +1발)
    if (caster.role === 'ranged_dps' && slot === 1) {
      caster.resources.sp -= actualSpCost;
      caster.cooldowns[slot] = scaled.cooldown;

      const arrowCount = 2 + Math.floor(effLevel / 10);
      const monsters = registry.getMonsters().filter((m) => !m.isDead && registry.getDistance(caster, m) <= scaled.range);
      if (monsters.length === 0) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `🏹 [멀티샷 Lv.${effLevel}] 사거리 내 적이 없습니다!`,
          category: 'system',
        });
        return false;
      }

      for (let i = 0; i < arrowCount; i++) {
        const m = monsters[i % monsters.length];
        EventBus.emit('PROJECTILE_SPAWNED', {
          type: 'arrow',
          fromX: caster.x,
          fromY: caster.y,
          toX: m.x + (Math.random() - 0.5) * 0.4,
          toY: m.y + (Math.random() - 0.5) * 0.4,
          speed: 14.0,
        });
        const dmg = Math.round(scaled.power * metrics.physDamageMult * 0.75);
        this.applyDamage(caster, m, dmg, 'thrust', false, registry);
      }
      practiceSelfSkill(caster, 'thrust', 0.5);
      return true;
    }

    // [레인저 Slot 7: 스텔스]
    if (caster.role === 'ranged_dps' && slot === 7) {
      caster.resources.sp -= actualSpCost;
      caster.cooldowns[slot] = scaled.cooldown;
      caster.buffs = caster.buffs.filter((b) => b.id !== 'ranger_stealth');
      caster.buffs.push({
        id: 'ranger_stealth',
        name: `스텔스 Lv.${effLevel} (은신/회피+30%)`,
        sourceId: caster.id,
        concCost: 0,
        duration: 15.0,
        elapsed: 0,
        evadeBonus: 0.30,
        icon: '👤',
      });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `👤 [${caster.name}] [스텔스 Lv.${effLevel}] 발동! (재사용 대기시간: ${scaled.cooldown}초)`,
        category: 'system',
      });
      return true;
    }

    // [마법사 Slot 6: 마나쉴드] (방어력 증가 + 피해의 20~67%를 MP로 흡수)
    if (caster.role === 'caster_dps' && slot === 6) {
      caster.resources.mp -= actualMpCost;
      caster.cooldowns[slot] = scaled.cooldown;
      const defPct = (5 + (effLevel - 1) * 0.4) / 100;
      const absorbPct = Math.min(0.75, scaled.power / 100);
      caster.buffs = caster.buffs.filter((b) => b.id !== 'wizard_mana_shield');
      caster.buffs.push({
        id: 'wizard_mana_shield',
        name: `마나쉴드 Lv.${effLevel} (방어+${Math.round(defPct * 100)}%, MP흡수 ${Math.round(absorbPct * 100)}%)`,
        sourceId: caster.id,
        concCost: 0,
        duration: 60.0,
        elapsed: 0,
        damageReduction: defPct,
        manaShieldRatio: absorbPct,
        icon: '🔮',
      });
      EventBus.emit('SPELL_EFFECT_TRIGGERED', {
        type: 'buff_aura',
        gx: caster.x,
        gy: caster.y,
        color: '#818cf8',
      });
      return true;
    }

    // [마법사 Slot 7: 순간이동]
    if (caster.role === 'caster_dps' && slot === 7) {
      caster.resources.mp -= actualMpCost;
      caster.cooldowns[slot] = scaled.cooldown;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'blink_departure', gx: caster.x, gy: caster.y });

      const step = 4.5;
      let dx = 0, dy = 0;
      if (caster.dir.includes('E')) dx += step;
      if (caster.dir.includes('W')) dx -= step;
      if (caster.dir.includes('S')) dy += step;
      if (caster.dir.includes('N')) dy -= step;
      if (dx === 0 && dy === 0) dy += step;
      caster.x += dx;
      caster.y += dy;

      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'blink_arrival', gx: caster.x, gy: caster.y });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🌀 [${caster.name}] [순간이동 Lv.${effLevel}] 발동! (쿨타임: ${scaled.cooldown}초)`,
        category: 'combat',
      });
      return true;
    }

    // [클레릭 Slot 2: 인스턴트힐] & [클레릭 Slot 3: 인스턴트 그룹힐]
    if (caster.role === 'healer' && (slot === 2 || slot === 3)) {
      caster.resources.mp -= actualMpCost;
      caster.cooldowns[slot] = scaled.cooldown;

      if (slot === 2) {
        const healTarget = target && target.category !== 'monster' ? target : caster;
        const amount = Math.round(healTarget.resources.maxHp * 0.35 * metrics.healMult);
        healTarget.resources.hp = Math.min(healTarget.resources.maxHp, healTarget.resources.hp + amount);
        EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'heal', gx: healTarget.x, gy: healTarget.y, color: '#4ade80' });
        EventBus.emit('HEAL_APPLIED', {
          sourceId: caster.id,
          sourceName: caster.name,
          targetId: healTarget.id,
          targetName: healTarget.name,
          amount,
          isCrit: false,
        });
      } else {
        for (const ally of registry.getParty()) {
          if (ally.isDead) continue;
          const amount = Math.round(ally.resources.maxHp * 0.25 * metrics.healMult);
          ally.resources.hp = Math.min(ally.resources.maxHp, ally.resources.hp + amount);
          EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'heal', gx: ally.x, gy: ally.y, color: '#4ade80' });
          EventBus.emit('HEAL_APPLIED', {
            sourceId: caster.id,
            sourceName: caster.name,
            targetId: ally.id,
            targetName: ally.name,
            amount,
            isCrit: false,
          });
        }
      }
      practiceSelfSkill(caster, 'prayer', 0.5);
      return true;
    }

    // [바드 Slot 7: SOS] (파티 전원 10초간 HP 비례 이속 페널티 무시 + 이속 +100% 가속!)
    if (caster.role === 'buffer' && slot === 7) {
      caster.resources.mp -= actualMpCost;
      caster.cooldowns[slot] = scaled.cooldown;

      for (const ally of registry.getParty()) {
        if (ally.isDead) continue;
        ally.buffs = ally.buffs.filter((b) => b.id !== 'bard_sos');
        ally.buffs.push({
          id: 'bard_sos',
          name: `SOS 긴급질주 Lv.${effLevel} (부상페널티 무시 & 이속+100%)`,
          sourceId: caster.id,
          concCost: 0,
          duration: 10.0,
          elapsed: 0,
          moveSpeedBonus: 1.0,
          ignoreHpSpeedPenalty: true,
          icon: '🚀',
        });
        EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'buff_aura', gx: ally.x, gy: ally.y, color: '#f59e0b' });
      }

      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🚀 [${caster.name}] [SOS Lv.${effLevel}] 발동! 10초간 파티 전원이 부상 이속 감소를 무시하고 2배속으로 긴급 탈출합니다! (쿨타임: ${scaled.cooldown}초)`,
        category: 'system',
      });
      return true;
    }

    // ========================================================================
    // 👑 관리자 전용 자가 스킬 (Slot 3: 완치 - 타겟 미지정 시 시전자 자가 완치)
    // ========================================================================
    if (isAdmin && slot === 3 && !target) {
      caster.resources.hp = caster.resources.maxHp;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'heal', gx: caster.x, gy: caster.y, color: '#ec4899' });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💖 [관리자 완치] [${caster.name}] 자신의 체력을 100% 완전 회복(완치)했습니다!`,
        category: 'system',
      });
      return true;
    }

    // ========================================================================
    // 2. Targeted Instant Skills
    // ========================================================================
    if (!target) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚠️ [${def.nameKr}] 대상을 먼저 타겟팅하세요! (Tab 또는 클릭)`,
        category: 'system',
      });
      return false;
    }

    const dist = registry.getDistance(caster, target);
    if (dist > scaled.range) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚠️ [${def.nameKr}] 대상이 사거리 밖입니다! (${dist.toFixed(1)} / ${scaled.range}타일)`,
        category: 'system',
      });
      return false;
    }

    const isFriendly = target.category === 'companion' || target.category === 'player';
    caster.resources.sp -= actualSpCost;
    caster.resources.mp -= actualMpCost;
    if (!isAdmin) {
      caster.cooldowns[slot] = scaled.cooldown;
    }

    // ========================================================================
    // 👑 관리자 (Admin) 8종 전용 스킬 실행부
    // ========================================================================
    if (isAdmin) {
      // 1) 평타: 즉시 일반 데미지 공격 (쿨타임 0초)
      if (slot === 1) {
        this.applyDamage(caster, target, scaled.power, 'pure', isFriendly, registry);
        EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'shield_impact', gx: target.x, gy: target.y });
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `👑 [관리자 평타] [${target.name}] 타격! (${scaled.power} 피해)`,
          category: 'combat',
        });
        return true;
      }

      // 2) 주먹질: 무기를 들어도 데미지 고정 1!
      if (slot === 2) {
        if (!target.isDead) {
          target.resources.hp = Math.max(0, target.resources.hp - 1);
          EventBus.emit('COMBAT_HIT', {
            attackerId: caster.id,
            attackerName: caster.name,
            targetId: target.id,
            targetName: target.name,
            damage: 1,
            damageType: 'crush',
            isCrit: false,
            isFriendlyFire: isFriendly,
          });
          if (target.resources.hp <= 0) {
            target.isDead = true;
            target.cast = null;
            target.cc = null;
          }
        }
        EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'shield_impact', gx: target.x, gy: target.y });
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `👊 [관리자 주먹질] [${caster.name}] 이 [${target.name}] 에게 무기 무관 훈계 주먹질을 날렸습니다! (고정 데미지 1)`,
          category: 'combat',
        });
        return true;
      }

      // 3) 완치: 대상 체력 100% 즉시 완전 회복
      if (slot === 3) {
        target.resources.hp = target.resources.maxHp;
        EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'heal', gx: target.x, gy: target.y, color: '#ec4899' });
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `💖 [관리자 완치] [${target.name}] 의 체력을 100% 완치(풀피)시켰습니다! (${target.resources.maxHp} HP)`,
          category: 'system',
        });
        return true;
      }

      // 4) 스턴/해제: 토글 방식 (기절 중이면 즉시 해제, 아니면 600초 기절)
      if (slot === 4) {
        if (target.cc && target.cc.type === 'stun') {
          target.cc = null;
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `⚡ [관리자 스턴 해제] [${target.name}] 의 기절(Stun) 상태를 즉시 해제했습니다!`,
            category: 'system',
          });
        } else {
          CrowdControl.applyCC(target, 'stun', 600.0, caster.name);
          EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'shield_impact', gx: target.x, gy: target.y });
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `⚡ [관리자 스턴 부여] [${target.name}] 에게 강제 기절(Stun)을 걸었습니다!`,
            category: 'system',
          });
        }
        return true;
      }

      // 5) 메즈/해제: 토글 방식 (수면 중이면 즉시 깨움, 아니면 600초 수면)
      if (slot === 5) {
        if (target.cc && target.cc.type === 'mez') {
          target.cc = null;
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `💤 [관리자 메즈 해제] [${target.name}] 의 수면(Mez) 상태를 즉시 깨워 해제했습니다!`,
            category: 'system',
          });
        } else {
          CrowdControl.applyCC(target, 'mez', 600.0, caster.name);
          EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'buff_aura', gx: target.x, gy: target.y, color: '#38bdf8' });
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `💤 [관리자 메즈 부여] [${target.name}] 에게 강제 수면(Mez)을 걸었습니다!`,
            category: 'system',
          });
        }
        return true;
      }

      // 6) 슬로우: 토글 방식 (슬로우 중이면 해제, 아니면 75% 감속 걸기)
      if (slot === 6) {
        if (target.cc && target.cc.type === 'snare') {
          target.cc = null;
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `🐌 [관리자 슬로우 해제] [${target.name}] 의 감속(Slow) 상태를 해제하여 정상 속도로 복구했습니다!`,
            category: 'system',
          });
        } else {
          CrowdControl.applyCC(target, 'snare', 600.0, caster.name, 0.75);
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `🐌 [관리자 슬로우 부여] [${target.name}] 의 이동속도를 75% 대폭 감속시켰습니다!`,
            category: 'system',
          });
        }
        return true;
      }

      // 7) 감옥: 확인창(confirm) 후 지하 감옥 수감 격리 (이동/행동 완전 봉쇄)
      if (slot === 7) {
        const confirmed = typeof window !== 'undefined'
          ? window.confirm(`⚖️ [관리자 권한: 감옥 수감]\n\n대상 [${target.name}] (${target.job || target.category}) 캐릭터를 즉시 솔라리스 지하 감옥으로 수감 격리하시겠습니까?`)
          : true;

        if (!confirmed) {
          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `⛓️ [관리자 감옥] [${target.name}] 수감 집행을 취소했습니다.`,
            category: 'system',
          });
          return false;
        }

        // 대상 위치를 지하 3층 최심부 감옥 좌표(Zone 12 대묘당 혹은 Zone 10)나 구속 격리
        target.x = 10;
        target.y = 10;
        target.vx = 0;
        target.vy = 0;
        CrowdControl.applyCC(target, 'root', 999999, caster.name);
        EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'taunt_burst', gx: target.x, gy: target.y, radius: 2.0 });
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⛓️ [관리자 감옥 집행] [${target.name}] 이 솔라리스 지하 감옥에 수감 격리되었습니다! (영구 이동/탈출 불가 봉쇄)`,
          category: 'system',
        });
        return true;
      }

      // 8) 즉사: 타겟 대상 1방 즉사
      if (slot === 8) {
        target.resources.hp = 0;
        target.isDead = true;
        target.cast = null;
        target.cc = null;
        target.dotEffect = null;
        Concentration.releaseAllByCaster(target.id, registry.getAll());
        EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'lightning_strike', gx: target.x, gy: target.y });
        EventBus.emit('COMBAT_HIT', {
          attackerId: caster.id,
          attackerName: caster.name,
          targetId: target.id,
          targetName: target.name,
          damage: 999999,
          damageType: 'pure',
          isCrit: true,
          isFriendlyFire: isFriendly,
        });
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `☠️ [관리자 즉사] [${caster.name}] 이 [${target.name}] 을 천벌로 즉사시켰습니다!`,
          category: 'combat',
        });
        return true;
      }
    }

    // ========================================================================
    // 🐣 초보자 (Novice) 전용 스킬 (Slot 1: 기본 공격)
    // ========================================================================
    if (isNovice && slot === 1) {
      const dmg = Math.round(scaled.power * metrics.physDamageMult);
      this.applyDamage(caster, target, dmg, 'slash', isFriendly, registry);
      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'slash_impact', gx: target.x, gy: target.y });
      practiceSelfSkill(caster, 'slash', 0.2);
      return true;
    }

    // [팔라딘 Slot 6: 풀 (Pull)] - 원거리 견인 + 소량 어그로 증가 + 개인방어 추가 증가(전투종료시까지, 중복X)
    if (caster.role === 'tank' && slot === 6) {
      // 1) Pull enemy toward Paladin
      const dx = caster.x - target.x;
      const dy = caster.y - target.y;
      const len = Math.hypot(dx, dy) || 1;
      const pullDist = Math.max(0, dist - 1.3);
      target.x += (dx / len) * pullDist;
      target.y += (dy / len) * pullDist;

      // 2) Add Threat
      const pullThreat = 150 + (effLevel - 1) * 15;
      if (target.category === 'monster') {
        ThreatTable.addThreat(target, caster.id, pullThreat);
        target.inCombat = true;
        caster.inCombat = true;
      }

      // 3) Grant Non-stacking Personal Defense Buff until combat ends!
      const defBonusPct = (5 + (effLevel - 1) * 0.5) / 100;
      const existingPullBuff = caster.buffs.find((b) => b.id === 'paladin_pull_def');
      if (!existingPullBuff) {
        caster.buffs.push({
          id: 'paladin_pull_def',
          name: `풀(Pull) 수호방어 (+${(defBonusPct * 100).toFixed(1)}% 방어, 전투종료시까지/중복불가)`,
          sourceId: caster.id,
          concCost: 0,
          duration: -1,
          elapsed: 0,
          damageReduction: defBonusPct,
          buffCategory: 'pull_def',
          icon: '🧲',
        });
      }

      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'shield_impact', gx: target.x, gy: target.y });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🧲 [${caster.name}] [풀(Pull) Lv.${effLevel}]로 [${target.name}] 견인! (어그로 +${pullThreat}, 전투 종료 시까지 개인방어 +${(defBonusPct * 100).toFixed(1)}% 적용 — 중복불가)`,
        category: 'threat',
      });
      return true;
    }

    // [팔라딘 Slot 4: 방패슬램]
    if (caster.role === 'tank' && slot === 4) {
      const dmg = Math.round(scaled.power * metrics.physDamageMult);
      this.applyDamage(caster, target, dmg, 'crush', isFriendly, registry);
      CrowdControl.applyCC(target, 'stun', scaled.duration, caster.name);
      practiceSelfSkill(caster, 'crush', 0.4);
      return true;
    }

    // [레인저 Slot 5: 마크 (방어력 감소)]
    if (caster.role === 'ranged_dps' && slot === 5) {
      const armorShred = scaled.power / 100; // -6% ~ -35.5%
      target.armorDebuffPct = armorShred;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'taunt_burst', gx: target.x, gy: target.y, radius: 1.5 });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `👁️ [${caster.name}] 이 [${target.name}] 에게 [마크 Lv.${effLevel}] 지정! (방어력 -${scaled.power.toFixed(1)}% 감소)`,
        category: 'combat',
      });
      return true;
    }

    // [레인저 Slot 6: 트랩] & [수도승 Slot 2: 다리걸기] (이동속도 감소 Snare)
    if ((caster.role === 'ranged_dps' && slot === 6) || (caster.role === 'monk_hybrid' && slot === 2)) {
      const snarePct = Math.min(0.75, (20 + (effLevel - 1) * 0.8) / 100);
      if (caster.role === 'monk_hybrid') {
        const dmg = Math.round((20 + (effLevel - 1) * 2) * metrics.physDamageMult);
        this.applyDamage(caster, target, dmg, 'crush', isFriendly, registry);
      }
      CrowdControl.applyCC(target, 'snare', scaled.duration || 6.0, caster.name, snarePct);
      return true;
    }

    // [바드 Slot 6: 특급배달] (DAoC 민스트럴식 인스턴트 스턴!)
    if (caster.role === 'buffer' && slot === 6) {
      EventBus.emit('PROJECTILE_SPAWNED', {
        type: 'sound_wave',
        fromX: caster.x,
        fromY: caster.y,
        toX: target.x,
        toY: target.y,
        speed: 18.0,
      });
      this.applyDamage(caster, target, scaled.power, 'crush', isFriendly, registry);
      CrowdControl.applyCC(target, 'stun', scaled.duration, caster.name);
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `📯 [${caster.name}] 의 [특급배달 Lv.${effLevel}] 즉시 발동! [${target.name}] ${scaled.duration}초 인스턴트 스턴!`,
        category: 'combat',
      });
      return true;
    }

    return true;
  }

  /**
   * Complete casting when timer finishes
   */
  public completeCast(caster: EntityState, registry: EntityRegistry): void {
    if (!caster.cast) return;
    const { skillId, targetId } = caster.cast;
    const effLevel = caster.cast.castLevel || getEffectiveSkillLevel(caster, skillId);
    caster.cast = null;

    const skillSet = CLASS_SKILL_DEFINITIONS[caster.role] || CLASS_SKILL_DEFINITIONS.tank;
    const def = skillSet[skillId] || SKILL_DEFINITIONS[skillId];
    if (!def) return;

    const scaled = getScaledSkillMetrics(def, effLevel);
    const metrics = getDerivedCombatMetrics(caster);
    const target = targetId ? registry.get(targetId) : caster;
    caster.cooldowns[skillId] = scaled.cooldown;

    // 1. [레이즈데드] (Paladin Slot 7 / Monk Slot 8)
    if (def.name === 'Raise Dead') {
      if (!target) return;
      target.isDead = false;
      target.resources.hp = Math.round(target.resources.maxHp * 0.35);
      target.resources.mp = Math.round(target.resources.maxMp * 0.25);
      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'heal', gx: target.x, gy: target.y, color: '#fde047' });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `✨ [${caster.name}] 의 [레이즈데드 Lv.${effLevel}] 성공! [${target.name}] 부활! (다음 쿨타임: ${scaled.cooldown}초)`,
        category: 'heal',
      });
      return;
    }

    // 2. [클레릭 Slot 1: 홀리핸드]
    if (caster.role === 'healer' && skillId === 1) {
      if (!target || target.isDead) return;
      const healAmount = Math.round(scaled.power * metrics.healMult);
      target.resources.hp = Math.min(target.resources.maxHp, target.resources.hp + healAmount);
      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'heal', gx: target.x, gy: target.y, color: '#4ade80' });
      EventBus.emit('HEAL_APPLIED', {
        sourceId: caster.id,
        sourceName: caster.name,
        targetId: target.id,
        targetName: target.name,
        amount: healAmount,
        isCrit: false,
      });
      ThreatTable.distributeHealingThreat(registry.getMonsters(), caster.id, healAmount);
      practiceSelfSkill(caster, 'prayer', 0.6);
      return;
    }

    // 3. [클레릭 Slot 4: 클렌징]
    if (caster.role === 'healer' && skillId === 4) {
      if (!target || target.isDead) return;
      target.cc = null;
      target.dotEffect = null;
      target.armorDebuffPct = 0;
      EventBus.emit('SPELL_EFFECT_TRIGGERED', { type: 'heal', gx: target.x, gy: target.y, color: '#38bdf8' });
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `✨ [${caster.name}] 의 [클렌징 Lv.${effLevel}]! [${target.name}] 의 모든 디버프와 상태이상을 정화했습니다!`,
        category: 'heal',
      });
      return;
    }

    if (!target || target.isDead) return;
    const isFriendly = target.category === 'companion' || target.category === 'player';

    // 4. [레인저 Slot 2: 크리티컬샷]
    if (caster.role === 'ranged_dps' && skillId === 2) {
      EventBus.emit('PROJECTILE_SPAWNED', {
        type: 'arrow',
        fromX: caster.x,
        fromY: caster.y,
        toX: target.x,
        toY: target.y,
        speed: 16.0,
      });
      const critChance = Math.min(0.95, 0.15 + (effLevel - 1) * 0.008 + metrics.physCritChance);
      const isCrit = Math.random() < critChance;
      const critMult = isCrit ? 1.8 + (effLevel - 1) * 0.02 : 1.0;
      const dmg = Math.round(scaled.power * metrics.physDamageMult * critMult);
      this.applyDamage(caster, target, dmg, 'thrust', isFriendly, registry, isCrit);
      practiceSelfSkill(caster, 'thrust', 0.6);
      return;
    }

    // 5. [마법사 Slot 1: 메테오] (운석 갯수 + 화염 DoT)
    if (caster.role === 'caster_dps' && skillId === 1) {
      const meteorCount = 2 + Math.floor(effLevel / 10);
      const dotPerSec = (10 + (effLevel - 1) * 2.5) * metrics.magicDamageMult;
      EventBus.emit('PROJECTILE_SPAWNED', {
        type: 'fireball',
        fromX: caster.x,
        fromY: caster.y,
        toX: target.x,
        toY: target.y,
        speed: 13.0,
      });

      for (const m of registry.getMonsters()) {
        if (!m.isDead && registry.getDistance(target, m) <= 2.8) {
          const dmg = Math.round(scaled.power * (0.7 + meteorCount * 0.2) * metrics.magicDamageMult);
          this.applyDamage(caster, m, dmg, 'holy', false, registry);
          m.dotEffect = {
            damagePerSec: dotPerSec,
            remaining: 5.0,
            tickAccum: 0,
            sourceId: caster.id,
            sourceName: caster.name,
          };
        }
      }
      practiceSelfSkill(caster, 'experiment', 0.7);
      return;
    }

    // 6. [마법사 Slot 2: 바위투사] (낮은 데미지 + 강력한 이동불가 루트 CC)
    if (caster.role === 'caster_dps' && skillId === 2) {
      const dmg = Math.round(scaled.power * metrics.magicDamageMult);
      this.applyDamage(caster, target, dmg, 'crush', isFriendly, registry);
      CrowdControl.applyCC(target, 'root', scaled.duration, caster.name);
      practiceSelfSkill(caster, 'experiment', 0.5);
      return;
    }

    // 7. [마법사 Slot 3: 체인라이트닝] (다단히트 감전 CC + 단일 1체일 때 2.0~3.8배 데미지 증폭!)
    if (caster.role === 'caster_dps' && skillId === 3) {
      const maxTargets = 2 + Math.floor(effLevel / 10);
      const nearbyEnemies = registry
        .getMonsters()
        .filter((m) => !m.isDead && registry.getDistance(target, m) <= 3.5)
        .slice(0, maxTargets);

      const isSingleTarget = nearbyEnemies.length <= 1;
      const singleAmpMult = isSingleTarget ? 2.0 + (effLevel - 1) * 0.03 : 1.0;

      const targetsToHit = nearbyEnemies.length > 0 ? nearbyEnemies : [target];
      for (const m of targetsToHit) {
        EventBus.emit('PROJECTILE_SPAWNED', {
          type: 'lightning',
          fromX: caster.x,
          fromY: caster.y,
          toX: m.x,
          toY: m.y,
          speed: 18.0,
        });
        const dmg = Math.round(scaled.power * metrics.magicDamageMult * singleAmpMult);
        this.applyDamage(caster, m, dmg, 'holy', isFriendly, registry, isSingleTarget);
        CrowdControl.applyCC(m, 'stun', scaled.duration, caster.name);
      }

      if (isSingleTarget) {
        EventBus.emit('LOG_EMITTED', {
          id: Math.random().toString(),
          timestamp: performance.now(),
          text: `⚡ [체인라이트닝 단일 집중!] 주변에 다른 적이 없어 [${target.name}] 에게 전류가 ${singleAmpMult.toFixed(2)}배 증폭되었습니다!`,
          category: 'combat',
        });
      }
      practiceSelfSkill(caster, 'experiment', 0.6);
      return;
    }

    // 8. [마법사 Slot 4: 프로스트윈드] (광역 감속 CC)
    if (caster.role === 'caster_dps' && skillId === 4) {
      const snarePct = Math.min(0.80, (25 + (effLevel - 1) * 0.8) / 100);
      for (const m of registry.getMonsters()) {
        if (!m.isDead && registry.getDistance(target, m) <= 3.0) {
          this.applyDamage(caster, m, Math.round(20 * metrics.magicDamageMult), 'holy', false, registry);
          CrowdControl.applyCC(m, 'snare', 6.0, caster.name, snarePct);
        }
      }
      practiceSelfSkill(caster, 'experiment', 0.5);
      return;
    }

    // 9. [바드 Slot 1: 귓속말 (단일메즈)] & [바드 Slot 2: 자장가 (광역메즈)]
    if (caster.role === 'buffer' && (skillId === 1 || skillId === 2)) {
      if (skillId === 1) {
        CrowdControl.applyCC(target, 'mez', scaled.duration, caster.name);
      } else {
        const radius = scaled.range + metrics.songRadiusBonus;
        for (const m of registry.getMonsters()) {
          if (!m.isDead && registry.getDistance(target, m) <= radius) {
            CrowdControl.applyCC(m, 'mez', scaled.duration, caster.name);
          }
        }
      }
      practiceSelfSkill(caster, 'performance', 0.6);
      return;
    }
  }

  /**
   * Interrupt cast immediately upon taking damage or moving
   */
  public interruptCast(caster: EntityState, reason: 'damage' | 'movement'): void {
    if (!caster.cast) return;
    const spellName = caster.cast.skillName;
    caster.cast = null;

    EventBus.emit('CAST_INTERRUPTED', {
      entityId: caster.id,
      entityName: caster.name,
      spellName,
      reason,
    });
  }

  /**
   * Central damage application with Evasion (Block/Parry/Evade), Armor Debuff (Mark),
   * Mana Shield absorption, Mez break, and Threat
   */
  public applyDamage(
    attacker: EntityState,
    target: EntityState,
    rawDamage: number,
    damageType: DamageType,
    isFriendlyFire: boolean,
    registry: EntityRegistry,
    forceCrit = false
  ): void {
    if (target.isDead) return;

    // 0. GM God Mode check: if target has godMode enabled, completely ignore damage
    const gm = (window as any).GM || (window as any).TerraAnimaGM;
    if (gm?.cheats?.godMode) {
      const player = registry.getPlayer();
      if (player && (target.id === player.id || target.category === 'companion')) {
        return;
      }
    }

    // 0b. GM Insta-Kill check: if attacker is player with instaKill enabled, deal massive fatal damage
    let finalRaw = rawDamage;
    let isGmInsta = false;
    if (gm?.cheats?.instaKill) {
      const player = registry.getPlayer();
      if (player && attacker.id === player.id) {
        finalRaw = 99999;
        isGmInsta = true;
      }
    }

    const targetMetrics = getDerivedCombatMetrics(target);
    const attackerMetrics = getDerivedCombatMetrics(attacker);

    // 1. Check Evasion (Evade / Parry / Block) if target is awake (Bypassed by GM Insta-Kill)
    if (!isGmInsta && CrowdControl.canAct(target) && Math.random() < targetMetrics.evadeChance * 0.35) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `💨 [${target.name}] 이 민첩하게 공격을 회피(Evade/Parry/Block)했습니다!`,
        category: 'combat',
      });
      return;
    }

    let finalDamage = finalRaw;

    // 2. Check Critical Hit
    const isCrit =
      forceCrit ||
      Math.random() < (damageType === 'holy' ? attackerMetrics.magicCritChance : attackerMetrics.physCritChance);
    if (isCrit && !forceCrit) {
      finalDamage = Math.round(finalDamage * 1.5);
    }

    // 3. Apply Ranger's Mark Armor Reduction Debuff
    if (target.armorDebuffPct && target.armorDebuffPct > 0) {
      finalDamage = Math.round(finalDamage * (1 + target.armorDebuffPct));
    }

    // 4. Apply Defense Buffs (Monk Base Defense Buff + Paladin Pull Defense Buff)
    let totalDefReduction = 0;
    for (const b of target.buffs) {
      if (b.damageReduction && b.damageReduction > 0) {
        totalDefReduction += b.damageReduction;
      }
    }
    if (totalDefReduction > 0) {
      finalDamage = Math.max(1, Math.floor(finalDamage * (1 - Math.min(0.65, totalDefReduction))));
    }

    // 5. Apply Wizard Mana Shield (Absorb % of damage with MP instead of HP!)
    const manaShield = target.buffs.find((b) => b.manaShieldRatio && b.manaShieldRatio > 0);
    if (manaShield && manaShield.manaShieldRatio && target.resources.mp > 0) {
      const mpAbsorb = Math.min(target.resources.mp, Math.floor(finalDamage * manaShield.manaShieldRatio));
      target.resources.mp -= mpAbsorb;
      finalDamage = Math.max(1, finalDamage - mpAbsorb);
    }

    // Apply HP loss
    target.resources.hp = Math.max(0, target.resources.hp - finalDamage);

    // Break Mez if target was sleeping
    CrowdControl.onTakeDamage(target, finalDamage, attacker.name);

    // Interrupt casting if target was casting
    if (target.cast) {
      this.interruptCast(target, 'damage');
    }

    // Emit combat hit event
    EventBus.emit('COMBAT_HIT', {
      attackerId: attacker.id,
      attackerName: attacker.name,
      targetId: target.id,
      targetName: target.name,
      damage: finalDamage,
      damageType,
      isCrit,
      isFriendlyFire,
    });

    // Add threat if target is monster
    if (target.category === 'monster') {
      ThreatTable.addThreat(target, attacker.id, finalDamage);
      target.inCombat = true;
      attacker.inCombat = true;
    }

    // Handle death
    if (target.resources.hp <= 0) {
      target.isDead = true;
      target.cast = null;
      target.cc = null;
      target.dotEffect = null;
      Concentration.releaseAllByCaster(target.id, registry.getAll());

      EventBus.emit('UNIT_DIED', {
        victimId: target.id,
        killerId: attacker.id,
      });

      for (const e of registry.getAll()) {
        if (e.targetId === target.id) {
          e.targetId = null;
        }
      }

      if (target.category === 'monster') {
        registry.markForDespawn(target.id, 4.0);
      }
    }
  }

  /**
   * Update cooldowns, cast timers, and status effects
   */
  public update(entity: EntityState, dt: number, registry: EntityRegistry): void {
    if (entity.isDead) return;

    // Remove Paladin Pull defense buff when out of combat!
    if (!entity.inCombat && entity.buffs.some((b) => b.id === 'paladin_pull_def')) {
      entity.buffs = entity.buffs.filter((b) => b.id !== 'paladin_pull_def');
    }

    // Tick cooldowns
    for (const slotKey in entity.cooldowns) {
      const s = Number(slotKey);
      if (entity.cooldowns[s] > 0) {
        entity.cooldowns[s] = Math.max(0, entity.cooldowns[s] - dt);
      }
    }

    // Tick crowd control
    CrowdControl.update(entity, dt);

    // Tick casting progress
    if (entity.cast) {
      entity.cast.progress += dt;
      if (entity.cast.progress >= entity.cast.castTime) {
        this.completeCast(entity, registry);
      }
    }
  }
}

export const Skills = new SkillEngine();
